import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { getHelpHtml } from '../utils/help/help-content';

type AliasChipsFieldProps = {
  aliases: string[];
  onAdd: (name: string) => void;
  onRemove: (index: number) => void;
  placeholder?: string;
  /** Show the brand-aliases help tooltip + info box (step 1). */
  showHelp?: boolean;
};

export function AliasChipsField({
  aliases,
  onAdd,
  onRemove,
  placeholder = 'مثلاً: Mizfa Tools',
  showHelp = false,
}: AliasChipsFieldProps) {
  const [draft, setDraft] = useState('');

  function commit() {
    const value = draft.trim();
    if (!value) return;
    onAdd(value);
    setDraft('');
  }

  return (
    <Form.Group>
      <Form.Label className="fs--1 fw-medium d-inline-flex align-items-center gap-1 mb-1">
        نام‌های مستعار برند
        <span className="fw-normal text-500">(اختیاری)</span>
        {showHelp && <HelpTooltip html={getHelpHtml('brand_aliases')} />}
      </Form.Label>
      {showHelp && (
        <div className="rounded-3 border bg-100 dark__bg-1100 px-3 py-2 mb-2">
          <p className="mb-1 fs--1 text-600">
            نام‌های دیگری که ممکن است برای برند شما (و AI) استفاده شود — مثلاً فارسی، انگلیسی یا با فاصله
            متفاوت. وارد کردن این بخش الزامی نیست و می‌توانید بعداً از تنظیمات اضافه کنید.
          </p>
          <p className="mb-0 fs--1 text-600">
            مثال: <span className="fw-medium text-700">Mizfa Tools</span>,{' '}
            <span className="fw-medium text-700">میزفا تولز</span>,{' '}
            <span className="fw-medium text-700">mizfa tools</span>
          </p>
        </div>
      )}
      <div className="mb-2 d-flex gap-2">
        <Form.Control
          size="sm"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            commit();
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline-primary"
          disabled={!draft.trim()}
          onClick={commit}
          className="flex-shrink-0"
        >
          افزودن
        </Button>
      </div>
      {aliases.length > 0 && (
        <ul className="list-unstyled d-flex flex-wrap gap-2 mb-0">
          {aliases.map((alias, index) => (
            <li key={`${alias}-${index}`}>
              <span className="d-inline-flex align-items-center gap-1 rounded-pill border border-200 dark__border-400 bg-200 dark__bg-1000 py-0 ps-2 pe-0 fs--2 text-600">
                <span className="text-truncate" style={{ maxWidth: 180 }}>
                  {alias}
                </span>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => onRemove(index)}
                  className="rounded-circle p-0 text-500"
                  aria-label={`حذف ${alias}`}
                  title="حذف نام مستعار"
                >
                  <FontAwesomeIcon icon="times" className="fs--2" />
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Form.Group>
  );
}
